/**
 * CACHE-AND-QUEUE — saha adımlarının çevrimdışı kuyruğu (F3·S5; tasarım
 * tutanağı T2: "hepsi cache-and-queue kapsamında — kilitli: izin açma ofis
 * işidir").
 *
 * ═══ KAPSAM KİLİTLİ ═══
 * Kuyruğa YALNIZ KAPANIŞ YÖNLÜ, güvenli adımlar girer:
 *   askı al · izolasyon uygula/doğrula · kişisel kilit as/çıkar ·
 *   "işim bitti" · ekipman kimlik teyidi (K11).
 *
 * ÇEVRİMİÇİ İSTEYENLER (bilinçli DIŞARIDA): işbaşı doğrulaması (aktife
 * geçiş) · askıdan dönüş · devir kabulü · geri çekilme · İZOLASYON KALDIRMA ·
 * zorla kilit kaldırma. Gerekçe tek cümle: **bayat veriyle riskli yönde
 * işlem yapılmaz** — canlı uygunluk/yetki kararı gerektiren ya da enerjiyi
 * geri veren adım, sunucuya sorulmadan yapılmış sayılamaz. Şüphede olan
 * adım çevrimiçi tarafta bırakılır (izolasyon kaldırma bu yüzden burada
 * DEĞİL: kilit zinciri sunucuda çözülür).
 *
 * ═══ ÇATIŞMA KURALI ═══
 * Kuyruktaki işlem sunucuya ulaştığında durum makinesinden GEÇEMİYORSA
 * (409/422/403) reddedilir ve ÇATIŞMA LİSTESİNE düşer — sessiz kayıp YOKTUR.
 * Ağ hatasında kayıt kuyrukta KALIR (sıra korunur, ilk hatada durulur).
 *
 * ═══ İKİ SAATLİ İZ ═══
 * Her kayıt kuyruğa alınırken CİHAZ saatiyle damgalanır (`cihazAt`) ve
 * sunucuya öyle gider; sunucu kendi saatini ayrıca yazar. Hangisinin ne
 * zaman olduğu sonradan sorulabilir.
 *
 * ═══ TEKNİK YERLEŞİM (executor kararı — session-log şerhi) ═══
 * Depo `localStorage`tır: kuyruk küçük JSON satırlarından oluşur, senkron
 * okunur ve sayfa yenilemesine dayanır. Service worker + IndexedDB'ye geçiş
 * kaydın içine DOSYA (foto) girdiğinde gerekir — mobil fazın işi.
 */

const KUYRUK_ANAHTARI = 'isg.saha.kuyruk';
const CATISMA_ANAHTARI = 'isg.saha.catismalari';

/** Kuyruğa alınabilen adımlar — LİSTE KAPALIDIR (kapsam kilidi kodda). */
export type SahaKuyrukIslem =
  | { tip: 'askiya_al'; permitId: string; gerekce?: string | null }
  | { tip: 'izolasyon_uygula'; permitId: string; izolasyonNoktasi: string; kilitNo?: string }
  | { tip: 'izolasyon_dogrula'; permitId: string; isolationId: string }
  | { tip: 'kilit_as'; permitId: string; isolationId: string }
  | { tip: 'kilit_cikar'; permitId: string; lockId: string }
  | { tip: 'isim_bitti'; permitId: string }
  | { tip: 'ekipman_teyit'; permitId: string; kod: string };

export interface KuyrukKaydi {
  id: string;
  islem: SahaKuyrukIslem;
  /** CİHAZ saati (ISO) — sunucu kaydı ayrı tutulur. */
  cihazAt: string;
}

export interface SahaCatismasi {
  id: string;
  islem: SahaKuyrukIslem;
  cihazAt: string;
  status: number;
  mesaj: string;
}

/** İşlem → uç adresi + gövde. Adres BURADA tek yerde durur. */
export function kuyrukIstegi(kayit: KuyrukKaydi): { path: string; body: unknown } {
  const { islem, cihazAt } = kayit;
  switch (islem.tip) {
    case 'askiya_al':
      return {
        path: `/api/ptw/permits/${islem.permitId}/suspend`,
        body: { gerekce: islem.gerekce ?? null, cihazAt },
      };
    case 'izolasyon_uygula':
      return {
        path: `/api/ptw/permits/${islem.permitId}/isolations`,
        body: {
          izolasyonNoktasi: islem.izolasyonNoktasi,
          kilitNo: islem.kilitNo ?? null,
          cihazAt,
        },
      };
    case 'izolasyon_dogrula':
      return { path: `/api/ptw/isolations/${islem.isolationId}/verify`, body: {} };
    case 'kilit_as':
      return { path: `/api/ptw/isolations/${islem.isolationId}/locks`, body: { cihazAt } };
    case 'kilit_cikar':
      return { path: `/api/ptw/isolation-locks/${islem.lockId}/remove`, body: { cihazAt } };
    case 'isim_bitti':
      return { path: `/api/ptw/permits/${islem.permitId}/work-completed`, body: { cihazAt } };
    case 'ekipman_teyit':
      return {
        path: `/api/ptw/permits/${islem.permitId}/equipment-scan`,
        body: { kod: islem.kod, cihazAt },
      };
  }
}

/** İnsan diline çeviri — çatışma listesi ne olduğunu söylemek zorunda. */
export const KUYRUK_ISLEM_ETIKET: Record<SahaKuyrukIslem['tip'], string> = {
  askiya_al: 'Askıya alma',
  izolasyon_uygula: 'İzolasyon uygulama',
  izolasyon_dogrula: 'İzolasyon doğrulama',
  kilit_as: 'Kişisel kilit asma',
  kilit_cikar: 'Kişisel kilit çıkarma',
  isim_bitti: '"İşim bitti" bildirimi',
  ekipman_teyit: 'Ekipman kimlik teyidi',
};

function oku<T>(anahtar: string): T[] {
  try {
    const ham = localStorage.getItem(anahtar);
    return ham ? (JSON.parse(ham) as T[]) : [];
  } catch {
    // Bozuk depo kuyruğu düşürmez: boş sayılır (kayıt zaten sunucuda yok).
    return [];
  }
}

function yaz<T>(anahtar: string, deger: T[]): void {
  localStorage.setItem(anahtar, JSON.stringify(deger));
  dinleyicileriUyar();
}

const dinleyiciler = new Set<() => void>();

export function kuyrugaAbone(dinleyici: () => void): () => void {
  dinleyiciler.add(dinleyici);
  return () => dinleyiciler.delete(dinleyici);
}

function dinleyicileriUyar(): void {
  for (const dinleyici of dinleyiciler) dinleyici();
}

export function kuyruguOku(): KuyrukKaydi[] {
  return oku<KuyrukKaydi>(KUYRUK_ANAHTARI);
}

export function catismalariOku(): SahaCatismasi[] {
  return oku<SahaCatismasi>(CATISMA_ANAHTARI);
}

export function catismalariTemizle(): void {
  yaz<SahaCatismasi>(CATISMA_ANAHTARI, []);
}

/** Kuyruğa al — CİHAZ saati burada damgalanır (iki saatli izin ilk yarısı). */
export function kuyrugaAl(islem: SahaKuyrukIslem): KuyrukKaydi {
  const kayit: KuyrukKaydi = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    islem,
    cihazAt: new Date().toISOString(),
  };
  yaz(KUYRUK_ANAHTARI, [...kuyruguOku(), kayit]);
  return kayit;
}

export interface SenkronSonucu {
  gonderilen: number;
  catisan: number;
  /** Ağ hâlâ kapalıysa kuyrukta kalan kayıt sayısı. */
  kalan: number;
}

/**
 * SIRAYLA gönderir (FIFO — saha adımlarının sırası anlamlıdır: kilit asma
 * ile çıkarma yer değiştiremez).
 *
 * - 2xx → kuyruktan düşer.
 * - 4xx → ÇATIŞMA: kuyruktan düşer, çatışma listesine yazılır (kullanıcı
 *   görür; sessiz kayıp yok). 401 istisnadır — oturum sorunudur, kayıt
 *   KALIR ve senkron durur.
 * - ağ hatası → kayıt kalır ve senkron durur (sıra bozulmasın).
 */
export async function senkronEt(
  gonder: (path: string, body: unknown) => Promise<{ ok: boolean; status: number; mesaj: string }>,
): Promise<SenkronSonucu> {
  let gonderilen = 0;
  let catisan = 0;
  const catismalar = catismalariOku();

  for (const kayit of kuyruguOku()) {
    const { path, body } = kuyrukIstegi(kayit);
    let sonuc: { ok: boolean; status: number; mesaj: string };
    try {
      sonuc = await gonder(path, body);
    } catch {
      break; // ağ yok: sıradakileri deneme, kuyruk korunur
    }
    if (sonuc.ok) {
      gonderilen += 1;
    } else if (sonuc.status === 401) {
      break; // oturum: kayıt kalır
    } else {
      catisan += 1;
      catismalar.push({
        id: kayit.id,
        islem: kayit.islem,
        cihazAt: kayit.cihazAt,
        status: sonuc.status,
        mesaj: sonuc.mesaj,
      });
    }
    yaz(
      KUYRUK_ANAHTARI,
      kuyruguOku().filter((satir) => satir.id !== kayit.id),
    );
  }

  if (catisan > 0) yaz(CATISMA_ANAHTARI, catismalar);
  return { gonderilen, catisan, kalan: kuyruguOku().length };
}
